import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, filter, switchMap, take, Subject, map, withLatestFrom, of, forkJoin, catchError } from 'rxjs';
import { environment } from '@environments/environment';
import { Local, LocalForm } from '@core/models';
import {
  ResponseSuccess,
  ResponseObjectSuccess,
} from '@interfaces/response-success';
import { Store } from '@ngrx/store';
import { selectAuthenticationIsReseller, selectAuthenticationIsSuperUser, selectCredential, selectStoreList, selectStoreListDropdowns } from '@store/selectors';
import { Credential } from '@core/models';
import { transformDropdown, transformStoreUpsert } from '@core/helpers';
import { MenuItem } from 'primeng/api';
import { LicenseService, StateService } from '..';
import { StateEnum } from '@core/enums';
import { objectToFormData } from '@core/helpers/formdata';
import { StoreFilter } from './interfaces/store-filter';

@Injectable({ providedIn: 'root' })
export class StoreService {
  credential$!: Observable<Credential | null>;
  storeList$: Observable<Local[] | null>;
  storeListDropdowns$: Observable<Local[] | null>;

  private createCompletedSource = new Subject<void>();
  createCompleted$ = this.createCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private removeCompletedSource = new Subject<void>();
  removeCompleted$ = this.removeCompletedSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store,
    private stateService: StateService,
    private licenseService: LicenseService,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.storeList$ = this.store.select(selectStoreList);
    this.storeListDropdowns$ = this.store.select(selectStoreListDropdowns);
  }

  createCompleted(): void {
    this.createCompletedSource.next();
    this.closeDialog();
  }

  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  removeCompleted(): void {
    this.removeCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id: string): Observable<ResponseSuccess<Local>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Local>>(
          `${environment.microConfiguraciones}/api/v1/locales/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  findAll({ name, resellerId, accountId, stateId, includesInactive = true }: StoreFilter): Observable<ResponseSuccess<Local>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      withLatestFrom(
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller)
      ),
      switchMap(([ credential, isSuperUser, isReseller ]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!name && { nombre: name }),
          ...(!!resellerId && { resellerId }),
          ...(!!accountId && { cuentaId: accountId }),
          ...(!!stateId && { stateId }),
          ...(!!isReseller && { resellerId: credential.idUsuario }),
          ...(!isSuperUser && !isReseller && { resellerId: credential.resellerId }),
          // ...(!isSuperUser && !isReseller && { resellerId: credential.resellerId, soporteId: credential.idUsuario }),
        };

        return this.http.get<ResponseSuccess<Local>>(
          `${environment.microConfiguraciones}/api/v1/locales`,
          {
            headers: { Authorization: token },
            params: { ...params, incluyeInactivos: includesInactive },
          }
        );
      }),
      take(1)
    );
  }

  filter(text: string): Observable<Local[]> {
    return this.storeList$.pipe(
      filter((storeList): storeList is Local[] => storeList !== null && storeList.length > 0),
      map((storeList) => storeList.filter((store) => store.nombre.toLowerCase().includes(text.toLowerCase()))),
      take(1),
    );
  }

  create(storeForm: LocalForm): Observable<ResponseSuccess<Local>> {
    return this.credential$.pipe(
      withLatestFrom(
        this.stateService.getStateByName(StateEnum.Active),
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller),
      ),
      filter(([credential]) => !!credential?.granted),
      filter(([_, state]) => !!state),
      switchMap(([ credential, state, _isSuperUser, _isReseller]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseSuccess<Local>>(
          `${environment.microConfiguraciones}/api/v1/locales`,
          objectToFormData(
            transformStoreUpsert({
              ...storeForm,
              statusId: state?.idEstado as string,
              // supportId: (!!isSuperUser || !!isReseller) ? storeForm.supportId : credential!.idUsuario,
            })
          ),
          { headers: { Authorization: token } }
        ).pipe(
          switchMap((response) => {
            const { data: [ { idLocal } ] } = response ?? { data: [ { idLocal: null } ] };

            return forkJoin([
              this.licenseService.upsert({
                ...storeForm,
                id: idLocal,
              }, true)
            ]).pipe(
              catchError((error) => of(error)),
              map((_) => response)
            )
          })
        )
      }),
      take(1)
    );
  }

  update(
    id: string,
    storeForm: LocalForm,
    active?: boolean,
  ): Observable<ResponseSuccess<Local>> {
    return this.credential$.pipe(
      withLatestFrom(
        this.stateService.getStateByName(StateEnum.Active),
        this.store.select(selectAuthenticationIsSuperUser),
        this.store.select(selectAuthenticationIsReseller),
      ),
      filter(([credential]) => !!credential?.granted),
      filter(([_, state]) => !!state),
      switchMap(([ credential, state, _isSuperUser, _isReseller]) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseSuccess<Local>>(
          `${environment.microConfiguraciones}/api/v1/locales/${id}`,
          objectToFormData(
            transformStoreUpsert({
              ...storeForm,
              statusId: !!active ? state?.idEstado : storeForm.statusId,
              // supportId: (!!isSuperUser || !!isReseller) ? storeForm.supportId : credential!.idUsuario,
            }),
          ),
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  activate({ id, storeForm }: { id: string, storeForm: LocalForm }): Observable<ResponseSuccess<Local>> {
    return this.update(id, storeForm, true).pipe(
      take(1)
    );
  }

  deactivate(id: string): Observable<ResponseObjectSuccess<Response>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<ResponseObjectSuccess<Response>>(
          `${environment.microConfiguraciones}/api/v1/locales/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  getDropdown<T extends keyof Local>(callRequest: boolean = false, elementId: T = 'idLocal' as T): Observable<MenuItem[]> {
    const storeList$ = !!callRequest
      ? this.findAll({}).pipe(map(({ data }) => (data)))
      : this.storeList$;

    return storeList$.pipe(
      filter((stores): stores is Local[] => !!stores),
      map((stores) => transformDropdown(stores, elementId!, 'nombre')),
    );
  }

  getDropdownAlternative(): Observable<MenuItem[]> {
    return this.storeListDropdowns$.pipe(
      filter((stores): stores is Local[] => !!stores),
      map((stores) => transformDropdown(stores, 'idLocal', 'nombre')),
    );
  }

  duplicate(originLocal?: string | null, targetLocal?: string | null): Observable<ResponseSuccess<Local>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted && !!originLocal && !!targetLocal),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseSuccess<Local>>(
          `${environment.microConfiguraciones}/api/v1/locales/duplicate-menu`,
            {
              localOrigenId: targetLocal,
              localDestinoId: originLocal
            },
          { headers: { Authorization: token } }
        )
      }),
      take(1)
    );
  }
}
